import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useSelector, useDispatch } from "react-redux";
import { updateRestaurantValues } from "store/actions/restaurant";
import OrderCompleteMessage from "./OrderCompleteMessage";
import SectionBlock from "../SectionBlock";
import ToggleRow from "../ToggleRow";

import { noWheelInputProps } from "util/utils";
const CHARACTER_LIMIT = 100;
const CHARACTER_LIMIT_BIG = 200;

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  stickyActions: {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  saveHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
  buttonWrapper: {
    position: "relative",
    display: "inline-flex",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const OnlineCheckoutSettings = (props) => {
  const { className, activeSection = 0, ...rest } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [hideSpecialRequirement, setHideSpecialRequirement] = useState(false);
  const [foodCourtPaymentRequired, setFoodCourtPaymentRequired] =
    useState(false);
  const [foodCourtPaymentOptional, setFoodCourtPaymentOptional] =
    useState(false);
  const [dineinTakeawayEnabled, setDineinTakeawayEnabled] = useState(false);
  const [foodCourtTakeawayEnabled, setFoodCourtTakeawayEnabled] =
    useState(false);
  const [isDineInMobilePhoneRequired, setIsDineInMobilePhoneRequired] =
    useState(false);
  const [isDineInGuestNumberRequired, setIsDineInGuestNumberRequired] =
    useState(false);
  const [isDineInNameHidden, setIsDineInNameHidden] = useState(false);
  const [isTillPaymentEnabled, setIsTillPaymentEnabled] = useState(false);
  const [isAdyenPaymentEnabled, setIsAdyenPaymentEnabled] = useState(false);
  const [isAdyenWalletOnly, setIsAdyenWalletOnly] = useState(false);
  const [isWechatPOSPaymentEnabled, setIsWechatPOSPaymentEnabled] =
    useState(false);
  const [isWechatOnlinePaymentEnabled, setIsWechatOnlinePaymentEnabled] =
    useState(false);
  const [
    isDisableAutoAcceptOrderWithoutLoc,
    setIsDisableAutoAcceptOrderWithoutLoc,
  ] = useState(false);
  const [
    dineinOnlinePayAtCounterCheckoutButtonTitle,
    setDineinOnlinePayAtCounterCheckoutButtonTitle,
  ] = useState("");

  const [allergyNotice, setAllergyNotice] = useState("");
  const [specialTerms, setSpecialTerms] = useState("");
  const [
    postpaidAutoAcceptAmountThreshold,
    setPostpaidAutoAcceptAmountThreshold,
  ] = useState(0);

  const [orderCompleteMessages, setOrderCompleteMessages] = useState(null);

  const [hideDeliveryTime, setHideDeliveryTime] = useState(false);
  const [isDeliveryCashPaymentEnabled, setIsDeliveryCashPaymentEnabled] =
    useState(false);

  const handleOrderCompleteMessagesChange = (newValues) => {
    setOrderCompleteMessages(newValues);
  };

  /** @returns {Promise<unknown>} */
  const updateRestaurant = (values) =>
    dispatch(updateRestaurantValues(values));
  const restaurant = useSelector(
    (state) => state.restaurant.selectedRestaurant
  );

  useEffect(() => {
    if (!restaurant) return;
    if (restaurant && restaurant.onlineSettings) {
      const onlineSettings = restaurant.onlineSettings;
      if (
        onlineSettings.hideSpecialRequirement !== null &&
        onlineSettings.hideSpecialRequirement !== undefined
      ) {
        setHideSpecialRequirement(onlineSettings.hideSpecialRequirement);
      }
      if (
        onlineSettings.foodCourtPaymentRequired !== null &&
        onlineSettings.foodCourtPaymentRequired !== undefined
      ) {
        setFoodCourtPaymentRequired(onlineSettings.foodCourtPaymentRequired);
      }
      if (
        onlineSettings.foodCourtPaymentOptional !== null &&
        onlineSettings.foodCourtPaymentOptional !== undefined
      ) {
        setFoodCourtPaymentOptional(onlineSettings.foodCourtPaymentOptional);
      }
      if (
        onlineSettings.dineinTakeawayEnabled !== null &&
        onlineSettings.dineinTakeawayEnabled !== undefined
      ) {
        setDineinTakeawayEnabled(onlineSettings.dineinTakeawayEnabled);
      }
      if (
        onlineSettings.foodCourtTakeawayEnabled !== null &&
        onlineSettings.foodCourtTakeawayEnabled !== undefined
      ) {
        setFoodCourtTakeawayEnabled(onlineSettings.foodCourtTakeawayEnabled);
      }
      if (
        onlineSettings.isDineInGuestNumberRequired !== null &&
        onlineSettings.isDineInGuestNumberRequired !== undefined
      ) {
        setIsDineInGuestNumberRequired(
          onlineSettings.isDineInGuestNumberRequired
        );
      }
      if (
        onlineSettings.isDineInMobilePhoneRequired !== null &&
        onlineSettings.isDineInMobilePhoneRequired !== undefined
      ) {
        setIsDineInMobilePhoneRequired(
          onlineSettings.isDineInMobilePhoneRequired
        );
      }
      if (
        onlineSettings.isDineInNameHidden !== null &&
        onlineSettings.isDineInNameHidden !== undefined
      ) {
        setIsDineInNameHidden(onlineSettings.isDineInNameHidden);
      }
      if (
        onlineSettings.isTillPaymentEnabled !== null &&
        onlineSettings.isTillPaymentEnabled !== undefined
      ) {
        setIsTillPaymentEnabled(onlineSettings.isTillPaymentEnabled);
      }
      if (
        onlineSettings.specialTerms !== null &&
        onlineSettings.specialTerms !== undefined
      ) {
        setSpecialTerms(onlineSettings.specialTerms);
      }
      if (
        onlineSettings.allergyNotice !== null &&
        onlineSettings.allergyNotice !== undefined
      ) {
        setAllergyNotice(onlineSettings.allergyNotice);
      }
      if (
        onlineSettings.postpaidAutoAcceptAmountThreshold !== null &&
        onlineSettings.postpaidAutoAcceptAmountThreshold !== undefined
      ) {
        setPostpaidAutoAcceptAmountThreshold(
          onlineSettings.postpaidAutoAcceptAmountThreshold
        );
      }
      if (
        onlineSettings.isAdyenPaymentEnabled !== null &&
        onlineSettings.isAdyenPaymentEnabled !== undefined
      ) {
        setIsAdyenPaymentEnabled(onlineSettings.isAdyenPaymentEnabled);
      }
      if (
        onlineSettings.isAdyenWalletOnly !== null &&
        onlineSettings.isAdyenWalletOnly !== undefined
      ) {
        setIsAdyenWalletOnly(onlineSettings.isAdyenWalletOnly);
      }
      if (
        restaurant.posSettings &&
        restaurant.posSettings.isWechatPOSPaymentEnabled !== null &&
        restaurant.posSettings.isWechatPOSPaymentEnabled !== undefined
      ) {
        setIsWechatPOSPaymentEnabled(
          restaurant.posSettings.isWechatPOSPaymentEnabled
        );
      }
      if (
        restaurant.posSettings &&
        restaurant.posSettings.isDisableAutoAcceptOrderWithoutLoc !== null &&
        restaurant.posSettings.isDisableAutoAcceptOrderWithoutLoc !== undefined
      ) {
        setIsDisableAutoAcceptOrderWithoutLoc(
          restaurant.posSettings.isDisableAutoAcceptOrderWithoutLoc
        );
      }
      if (
        onlineSettings.orderCompleteMessages !== null &&
        onlineSettings.orderCompleteMessages !== undefined
      ) {
        setOrderCompleteMessages(onlineSettings.orderCompleteMessages);
      }
      if (
        onlineSettings.isWechatOnlinePaymentEnabled !== null &&
        onlineSettings.isWechatOnlinePaymentEnabled !== undefined
      ) {
        setIsWechatOnlinePaymentEnabled(
          onlineSettings.isWechatOnlinePaymentEnabled
        );
      }
      if (
        onlineSettings.dineinOnlinePayAtCounterCheckoutButtonTitle !== null &&
        onlineSettings.dineinOnlinePayAtCounterCheckoutButtonTitle !== undefined
      ) {
        setDineinOnlinePayAtCounterCheckoutButtonTitle(
          onlineSettings.dineinOnlinePayAtCounterCheckoutButtonTitle
        );
      }
      if (
        onlineSettings.hideDeliveryTime !== null &&
        onlineSettings.hideDeliveryTime !== undefined
      ) {
        setHideDeliveryTime(onlineSettings.hideDeliveryTime);
      }
      if (
        onlineSettings.isDeliveryCashPaymentEnabled !== null &&
        onlineSettings.isDeliveryCashPaymentEnabled !== undefined
      ) {
        setIsDeliveryCashPaymentEnabled(
          onlineSettings.isDeliveryCashPaymentEnabled
        );
      }
    }
  }, [restaurant]);

  const isReadySubmit = () => true;

  const updateSettings = async (event) => {
    event.preventDefault();
    if (!isReadySubmit() || buttonLoading) return;

    setButtonLoading(true);
    let onlineSettings = {
      hideSpecialRequirement,
      orderCompleteMessages,
    };
    if (restaurant && restaurant.onlineSettings) {
      onlineSettings = {
        ...restaurant.onlineSettings,
        hideSpecialRequirement,
        foodCourtPaymentRequired,
        foodCourtPaymentOptional,
        dineinTakeawayEnabled,
        foodCourtTakeawayEnabled,
        isDineInMobilePhoneRequired,
        isDineInGuestNumberRequired,
        isDineInNameHidden,
        isTillPaymentEnabled,
        isAdyenPaymentEnabled,
        isAdyenWalletOnly,
        specialTerms,
        allergyNotice,
        postpaidAutoAcceptAmountThreshold,
        orderCompleteMessages,
        isWechatOnlinePaymentEnabled,
        dineinOnlinePayAtCounterCheckoutButtonTitle,
        hideDeliveryTime,
        isDeliveryCashPaymentEnabled,
      };
    }
    let posSettings = {};
    if (restaurant && restaurant.posSettings) {
      posSettings = {
        ...restaurant.posSettings,
        isDisableAutoAcceptOrderWithoutLoc,
        isWechatPOSPaymentEnabled,
      };
    }

    try {
      await updateRestaurant({ onlineSettings, posSettings });
    } finally {
      setButtonLoading(false);
    }
  };

  const handleSwitchChange = (event) => {
    const name = event.target.name;
    const value = event.target.checked;

    if (name === "hideSpecialRequirement") {
      setHideSpecialRequirement(value);
      return;
    }
    if (name === "foodCourtPaymentRequired") {
      setFoodCourtPaymentRequired(value);
      if (value) setFoodCourtPaymentOptional(false);
      return;
    }
    if (name === "foodCourtPaymentOptional") {
      setFoodCourtPaymentOptional(value);
      if (value) setFoodCourtPaymentRequired(false);
      return;
    }
    if (name === "dineinTakeawayEnabled") {
      setDineinTakeawayEnabled(value);
      return;
    }
    if (name === "foodCourtTakeawayEnabled") {
      setFoodCourtTakeawayEnabled(value);
      return;
    }
    if (name === "isDineInGuestNumberRequired") {
      setIsDineInGuestNumberRequired(value);
      return;
    }
    if (name === "isDineInMobilePhoneRequired") {
      setIsDineInMobilePhoneRequired(value);
      return;
    }
    if (name === "isDineInNameHidden") {
      setIsDineInNameHidden(value);
      return;
    }
    if (name === "isTillPaymentEnabled") {
      setIsTillPaymentEnabled(value);
      if (value === true) setIsAdyenPaymentEnabled(false);
      return;
    }
    if (name === "isAdyenPaymentEnabled") {
      setIsAdyenPaymentEnabled(value);
      if (value === true) setIsTillPaymentEnabled(false);
      return;
    }
    if (name === "isAdyenWalletOnly") {
      setIsAdyenWalletOnly(value);
      return;
    }
    if (name === "isDisableAutoAcceptOrderWithoutLoc") {
      setIsDisableAutoAcceptOrderWithoutLoc(value);
      return;
    }
    if (name === "isWechatPOSPaymentEnabled") {
      setIsWechatPOSPaymentEnabled(value);
      return;
    }
    if (name === "isWechatOnlinePaymentEnabled") {
      setIsWechatOnlinePaymentEnabled(value);
      return;
    }
    if (name === "hideDeliveryTime") {
      setHideDeliveryTime(value);
      return;
    }
    if (name === "isDeliveryCashPaymentEnabled") {
      setIsDeliveryCashPaymentEnabled(value);
      return;
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 0:
        return (
          <SectionBlock
            title="Dine-in checkout"
            subtitle="Requirements guests must complete before placing a dine-in order."
          >
            <Stack spacing={1.5}>
              <ToggleRow
                checked={dineinTakeawayEnabled}
                name="dineinTakeawayEnabled"
                onChange={handleSwitchChange}
                label="Enable dine-in takeaway"
                description="Let dine-in guests order takeaway from the same menu experience."
              />
              <ToggleRow
                checked={isDineInMobilePhoneRequired}
                name="isDineInMobilePhoneRequired"
                onChange={handleSwitchChange}
                label="Require mobile phone at checkout"
                description="Ask guests for a contact number before they can place the order."
              />
              <ToggleRow
                checked={isDineInGuestNumberRequired}
                name="isDineInGuestNumberRequired"
                onChange={handleSwitchChange}
                label="Require guest number at checkout"
                description="Guests must enter how many people are at the table."
              />
              <ToggleRow
                checked={isDineInNameHidden}
                name="isDineInNameHidden"
                onChange={handleSwitchChange}
                label="Hide name field at checkout"
                description="Dine-in guests can place an order without entering their name."
              />
              <ToggleRow
                checked={isDisableAutoAcceptOrderWithoutLoc}
                name="isDisableAutoAcceptOrderWithoutLoc"
                onChange={handleSwitchChange}
                label="Manual accept when location is missing"
                description="Orders without a resolved geo location are held for staff to accept manually."
              />
            </Stack>
            <Box mt={2.5}>
              <Grid container spacing={3}>
                <Grid item md={6} sm={8} xs={12}>
                  <TextField
                    id="dineinOnlinePayAtCounterCheckoutButtonTitle"
                    label="Pay at counter button title"
                    fullWidth
                    inputProps={{ maxLength: CHARACTER_LIMIT }}
                    value={dineinOnlinePayAtCounterCheckoutButtonTitle}
                    variant="outlined"
                    helperText={`Shown on the dine-in checkout button • ${dineinOnlinePayAtCounterCheckoutButtonTitle.length}/${CHARACTER_LIMIT}`}
                    placeholder="e.g. Pay At Counter"
                    onChange={(e) =>
                      setDineinOnlinePayAtCounterCheckoutButtonTitle(
                        e.target.value
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          </SectionBlock>
        );
      case 1:
        return (
          <SectionBlock
            title="Food court operations"
            subtitle="Payment-required and payment-optional are mutually exclusive."
          >
            <Stack spacing={1.5}>
              <ToggleRow
                checked={foodCourtPaymentRequired}
                name="foodCourtPaymentRequired"
                onChange={handleSwitchChange}
                label="Payment required"
                description="Guests must pay online before the kitchen receives the order."
              />
              <ToggleRow
                checked={foodCourtPaymentOptional}
                name="foodCourtPaymentOptional"
                onChange={handleSwitchChange}
                label="Payment optional"
                description="Guests may pay now or at the counter; the order is sent either way."
              />
              <ToggleRow
                checked={foodCourtTakeawayEnabled}
                name="foodCourtTakeawayEnabled"
                onChange={handleSwitchChange}
                label="Takeaway"
                description="Show a takeaway option for food-court guests."
              />
            </Stack>
          </SectionBlock>
        );
      case 2:
        return (
          <SectionBlock
            title="Ordering experience"
            subtitle="Small options that shape what guests see on the storefront."
          >
            <Stack spacing={1.5}>
              <ToggleRow
                checked={hideSpecialRequirement}
                name="hideSpecialRequirement"
                onChange={handleSwitchChange}
                label="Hide special requirements"
                description="Removes the free-text field where guests describe dish preferences and allergies."
              />
              {hideSpecialRequirement && (
                <Alert severity="warning">
                  Guests will not be able to enter requirements or food
                  allergies.
                </Alert>
              )}
            </Stack>
          </SectionBlock>
        );
      case 3:
        return (
          <SectionBlock
            title="Payment providers"
            subtitle="Enable one or more payment methods for online orders."
          >
            <Grid container spacing={1.5}>
              <Grid item md={4} sm={6} xs={12}>
                <ToggleRow
                  checked={isAdyenPaymentEnabled}
                  name="isAdyenPaymentEnabled"
                  onChange={handleSwitchChange}
                  label="Adyen"
                  description="Card payments processed through Adyen."
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12}>
                <ToggleRow
                  checked={isWechatPOSPaymentEnabled}
                  name="isWechatPOSPaymentEnabled"
                  onChange={handleSwitchChange}
                  label="WeChat POS"
                  description="In-store WeChat Pay handled through the POS."
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12}>
                <ToggleRow
                  checked={isWechatOnlinePaymentEnabled}
                  name="isWechatOnlinePaymentEnabled"
                  onChange={handleSwitchChange}
                  label="WeChat online"
                  description="Guests pay online via WeChat before pickup or delivery."
                />
              </Grid>
            </Grid>
            {isAdyenPaymentEnabled && (
              <Box mt={2.5}>
                <ToggleRow
                  checked={isAdyenWalletOnly}
                  name="isAdyenWalletOnly"
                  onChange={handleSwitchChange}
                  label="Adyen: Apple Pay and Google Pay only"
                  description="Online guests can pay with Apple Pay or Google Pay only; card entry is hidden."
                />
              </Box>
            )}
          </SectionBlock>
        );
      case 4:
        return (
          <SectionBlock
            title="Notices & terms"
            subtitle="Copy shown to guests during ordering."
          >
            <Grid container spacing={3}>
              <Grid item md={6} sm={8} xs={12}>
                <TextField
                  id="allergyNotice"
                  label="Allergy notice"
                  multiline
                  rows={4}
                  fullWidth
                  inputProps={{ maxLength: CHARACTER_LIMIT_BIG }}
                  value={allergyNotice}
                  variant="outlined"
                  helperText={`Shown on the dish detail screen • ${allergyNotice.length}/${CHARACTER_LIMIT_BIG}`}
                  placeholder="e.g. Please inform staff of any food allergies"
                  onChange={(e) => setAllergyNotice(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item md={6} sm={8} xs={12}>
                <TextField
                  id="specialTerms"
                  label="Special terms"
                  multiline
                  rows={4}
                  fullWidth
                  inputProps={{ maxLength: CHARACTER_LIMIT }}
                  value={specialTerms}
                  variant="outlined"
                  helperText={`Shown on the checkout screen • ${specialTerms.length}/${CHARACTER_LIMIT}`}
                  placeholder="e.g. All sales are final"
                  onChange={(e) => setSpecialTerms(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </SectionBlock>
        );
      case 6:
        return (
          <SectionBlock
            title="Delivery"
            subtitle="Options that control what delivery guests see and how they can pay."
          >
            <Stack spacing={1.5}>
              <ToggleRow
                checked={hideDeliveryTime}
                name="hideDeliveryTime"
                onChange={handleSwitchChange}
                label="Hide the delivery time estimate"
                description="Guests will not see an estimated delivery time on the storefront or checkout."
              />
              <ToggleRow
                checked={isDeliveryCashPaymentEnabled}
                name="isDeliveryCashPaymentEnabled"
                onChange={handleSwitchChange}
                label="Allow pay with cash on delivery"
                description="Guests can choose to pay the driver in cash instead of paying online."
              />
            </Stack>
          </SectionBlock>
        );
      case 5:
      default:
        return (
          <SectionBlock
            title="Order completion"
            subtitle="Post-order messaging and auto-accept rules."
          >
            <Grid container spacing={3}>
              <Grid item md={8} sm={10} xs={12}>
                <OrderCompleteMessage
                  onValueChange={handleOrderCompleteMessagesChange}
                  messages={orderCompleteMessages}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12}>
                <TextField
                  type="number"
                  inputProps={noWheelInputProps}
                  id="postpaidAutoAcceptAmountThreshold"
                  label="Postpaid auto-accept threshold"
                  value={postpaidAutoAcceptAmountThreshold}
                  variant="outlined"
                  fullWidth
                  helperText="Orders below this amount are auto-accepted."
                  placeholder="0"
                  onChange={(e) =>
                    setPostpaidAutoAcceptAmountThreshold(Number(e.target.value))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </SectionBlock>
        );
    }
  };

  return (
    <Card {...rest} className={`${classes.root} ${className || ""}`}>
      <form>
        <CardContent className={classes.content}>{renderSection()}</CardContent>

        <Box className={classes.stickyActions}>
          <Typography className={classes.saveHint}>
            Save applies changes across every section.
          </Typography>
          <Box className={classes.buttonWrapper}>
            <Button
              color="primary"
              variant="contained"
              onClick={updateSettings}
              disabled={!isReadySubmit() || buttonLoading}
              size="large"
            >
              Save changes
            </Button>
            {buttonLoading && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </Box>
        </Box>
      </form>
    </Card>
  );
};

OnlineCheckoutSettings.propTypes = {
  className: PropTypes.string,
  activeSection: PropTypes.number,
};

export default OnlineCheckoutSettings;
